import React, { useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountTree as TreeIcon,
  EuroSymbolSharp as EuroIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import Teachr from "../../../../Objects/models/Teachr";
import MlmRepository from "../../../../Repositories/MlmRepository";
import { MlmTreeNode } from "../../../../Objects/interfaces/MlmTypes";
import { useTailwindBrand } from "../../../../Components/shared";
import MlmMemberDetailDialog from "./MlmMemberDetailDialog";
import { formatEuro, mlmLevelColor, mlmNodeFullName } from "./mlmUtils";

// ─── Cartes métriques (GET /mlm/summary) ──────────────────────────────────────

const MetricCard = ({
  icon,
  value,
  label,
  color,
  fontFamily,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  color: string;
  fontFamily: string;
}) => (
  <Card variant="outlined" sx={{ p: 1.5, textAlign: "center", height: "100%", fontFamily }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 0.5 }}>
      <Box sx={{ color, display: "flex", "& svg": { fontSize: 20 } }}>{icon}</Box>
      <Typography variant="h6" fontWeight="bold" sx={{ color: "text.primary", fontFamily }}>
        {value}
      </Typography>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
      {label}
    </Typography>
  </Card>
);

// ─── Nœud repliable de l'arbre ────────────────────────────────────────────────
// Volontairement épuré : qui est dans mon réseau, à quel niveau, avec quel code.
// Les chiffres (cours, commissions) vivent dans la fiche détail, au clic.

const MlmNode = ({
  node,
  fontFamily,
  onSelect,
}: {
  node: MlmTreeNode;
  fontFamily: string;
  onSelect: (node: MlmTreeNode) => void;
}) => {
  const [expanded, setExpanded] = useState(node.level < 2);
  const isRoot = node.level === 0;
  const color = isRoot ? "#043f3a" : mlmLevelColor(node.level);
  const hasChildren = node.children.length > 0;

  return (
    <Box sx={{ minWidth: "fit-content" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          pl: 1.5,
          mb: 0.75,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: `${color}50`,
          borderLeft: `4px solid ${color}`,
          bgcolor: `${color}0d`,
          whiteSpace: "nowrap",
          width: "fit-content",
          cursor: isRoot ? "default" : "pointer",
          transition: "box-shadow 0.15s ease",
          "&:hover": isRoot ? {} : { boxShadow: 2 },
        }}
        onClick={isRoot ? undefined : () => onSelect(node)}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setExpanded((v) => !v);
            }}
            aria-label={expanded ? "Replier" : "Déplier"}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28 }} />
        )}

        {isRoot ? (
          <Chip size="small" icon={<StarIcon />} label="Vous" sx={{ bgcolor: color, color: "white", fontWeight: 600 }} />
        ) : (
          <Tooltip title={`Niveau ${node.level} — ${node.rate ?? "?"} % du prix HT des cours déclarés`}>
            <Chip size="small" label={`N${node.level}`} sx={{ bgcolor: color, color: "white", fontWeight: 600, minWidth: 44 }} />
          </Tooltip>
        )}

        <Typography variant="body2" fontWeight={600} sx={{ fontFamily }}>
          {mlmNodeFullName(node)}
        </Typography>

        {node.teachr.invitationCode && (
          <Tooltip title="Code de parrainage">
            <Chip
              size="small"
              variant="outlined"
              label={node.teachr.invitationCode}
              sx={{ fontFamily: "monospace", fontSize: 11, height: 20 }}
            />
          </Tooltip>
        )}

        {hasChildren && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily }}>
            {node.children.length} filleul{node.children.length > 1 ? "s" : ""}
          </Typography>
        )}
      </Box>

      {hasChildren && expanded && (
        <Box sx={{ pl: 3.5, borderLeft: "1px dashed", borderColor: "divider", ml: 2 }}>
          {node.children.map((child) => (
            <MlmNode key={child.teachr.id} node={child} fontFamily={fontFamily} onSelect={onSelect} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Vue principale (MIP-252) ─────────────────────────────────────────────────

const MlmTreeView = ({ teachr, emptyState }: { teachr: Teachr; emptyState?: React.ReactNode }) => {
  const { brandConfig } = useTailwindBrand();
  const fontFamily = brandConfig.fontFamily;
  const primaryHex = brandConfig.colors?.primary ?? "#0dcebf";
  const [selectedNode, setSelectedNode] = useState<MlmTreeNode | null>(null);

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["mlm", "summary", teachr?.id],
    queryFn: () => MlmRepository.getSummary(teachr.id as number),
    enabled: !!teachr?.id,
  });

  const { data: treeData, isLoading: treeLoading, isError } = useQuery({
    queryKey: ["mlm", "tree", teachr?.id],
    queryFn: () => MlmRepository.getTree(teachr.id as number),
    enabled: !!teachr?.id,
  });

  if (isError) {
    return <Alert severity="error">Impossible de charger votre arbre de parrainage. Veuillez réessayer plus tard.</Alert>;
  }

  // Réseau vide : la page d'invitation prend toute la place (0 filleul = rien à visualiser)
  const isEmpty = !treeLoading && (!treeData?.tree || treeData.tree.children.length === 0);
  if (isEmpty && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* 4 cartes métriques */}
      <Grid container spacing={2}>
        {summaryLoading ? (
          [0, 1, 2, 3].map((i) => (
            <Grid item xs={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={84} sx={{ borderRadius: 2 }} />
            </Grid>
          ))
        ) : (
          <>
            <Grid item xs={6} md={3}>
              <MetricCard icon={<GroupIcon />} value={summary?.networkSize ?? 0} label="Filleuls dans mon réseau" color={primaryHex} fontFamily={fontFamily} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard icon={<SchoolIcon />} value={summary?.coursesCount ?? 0} label="Cours pris par le réseau" color={primaryHex} fontFamily={fontFamily} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard icon={<EuroIcon />} value={formatEuro(summary?.commissionMonth)} label="Commission du mois" color={primaryHex} fontFamily={fontFamily} />
            </Grid>
            <Grid item xs={6} md={3}>
              <MetricCard icon={<EuroIcon />} value={formatEuro(summary?.commissionTotal)} label="Commission totale" color={primaryHex} fontFamily={fontFamily} />
            </Grid>
          </>
        )}
      </Grid>

      {/* Arbre interactif */}
      <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <TreeIcon sx={{ color: primaryHex }} />
          <Typography variant="h6" fontWeight={600} sx={{ fontFamily, color: primaryHex }}>
            Mon arbre de parrainage
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2, fontFamily }}>
          Cliquez sur un coach pour voir ses cours et vos commissions.
        </Typography>

        {treeLoading ? (
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
        ) : !treeData?.tree || treeData.tree.children.length === 0 ? (
          <Alert severity="info" sx={{ fontFamily }}>
            Votre réseau est vide pour le moment. Partagez votre code de parrainage pour développer votre équipe :
            chaque coach parrainé (et ses propres filleuls, sur deux niveaux) génère des commissions sur ses cours déclarés.
          </Alert>
        ) : (
          <>
            {/* Légende des niveaux */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {Object.keys(treeData.rates ?? {}).map(Number).sort((a, b) => a - b).map((level) => (
                <Chip
                  key={level}
                  size="small"
                  label={`Niveau ${level} : ${treeData.rates?.[level] ?? "?"}%`}
                  sx={{ bgcolor: `${mlmLevelColor(level)}1a`, color: mlmLevelColor(level), fontWeight: 600 }}
                />
              ))}
            </Box>
            {/* Scroll horizontal sur mobile */}
            <Box sx={{ overflowX: "auto", pb: 1 }}>
              <MlmNode node={treeData.tree} fontFamily={fontFamily} onSelect={setSelectedNode} />
            </Box>
          </>
        )}
      </Card>

      <MlmMemberDetailDialog node={selectedNode} open={selectedNode !== null} onClose={() => setSelectedNode(null)} />
    </Box>
  );
};

export default MlmTreeView;
